import { API_BASE_URL } from './api';

export interface OrderPickup {
  id?: number;
  userId: number;
  storeId: number;
  status: string;
  items: number;
  orderTotal?: number;
  deliveryFee?: number;
  doordashTrackingUrl?: string;
  barcodeUrl?: string;
  [key: string]: any;
}

export interface RefundRequest {
  selectedItems: number[];
  reason: string;
  otherReason?: string;
  description: string;
  refundAmount: number;
  contactPhone?: string;
}

export interface RefundProcess {
  refundAmount: number;
  refundMethod: string;
  refundReason?: string;
  adminNotes?: string;
}

/**
 * Create a new order pickup
 */
export const createOrderPickup = async (orderData: Partial<OrderPickup>): Promise<OrderPickup> => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE_URL}/api/order-pickups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include',
    body: JSON.stringify(orderData),
  });

  if (!response.ok) {
    throw new Error(`Failed to create order pickup: ${response.status}`);
  }

  return response.json();
};

/**
 * Get order pickup by ID
 */
export const getOrderPickupById = async (orderId: number): Promise<OrderPickup> => {
  const response = await fetch(`${API_BASE_URL}/api/order-pickups/${orderId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order pickup ${orderId}: ${response.status}`);
  }

  return response.json();
};

/**
 * Update order pickup by ID
 */
export const updateOrderPickup = async (orderId: number, updateData: Partial<OrderPickup>): Promise<OrderPickup> => {
  const response = await fetch(`${API_BASE_URL}/api/order-pickups/${orderId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    throw new Error(`Failed to update order pickup ${orderId}: ${response.status}`);
  }

  return response.json();
};

/**
 * Get all order pickups for a user
 */
export const getAllOrderPickups = async (userId: number): Promise<OrderPickup[]> => {
  const response = await fetch(`${API_BASE_URL}/api/order-pickups?userId=${userId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch order pickups for user ${userId}: ${response.status}`);
  }

  return response.json();
};

/**
 * Request refund for an order
 */
export const requestOrderRefund = async (orderId: number, refundData: RefundRequest): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/api/order-pickups/${orderId}/request-refund`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(refundData),
  });

  if (!response.ok) {
    throw new Error(`Failed to request refund for order ${orderId}: ${response.status}`);
  }

  return response.json();
};

/**
 * Process refund for an order (admin only)
 */
export const processOrderRefund = async (orderId: number, refundData: RefundProcess): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/api/order-pickups/${orderId}/process-refund`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(refundData),
  });

  if (!response.ok) {
    throw new Error(`Failed to process refund for order ${orderId}: ${response.status}`);
  }

  return response.json();
};
